import { useSyncExternalStore } from 'react';

/**
 * "Is a microphone being listened to right now, and by whom?" — one app-wide
 * signal so companions like Jot can react, and so dictation stays out of the
 * way of an encounter recording. Sessions register while live and release
 * after.
 */
export type ListeningSource = 'dictation' | 'recording';

const sessions: Record<ListeningSource, number> = { dictation: 0, recording: 0 };
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach(listener => listener());
}

/** Marks a live session for `source`. Call the returned function when it ends. */
export function markListening(source: ListeningSource): () => void {
  sessions[source] += 1;
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    sessions[source] -= 1;
    notify();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const snapshots: Record<ListeningSource | 'any', () => boolean> = {
  any: () => sessions.dictation + sessions.recording > 0,
  dictation: () => sessions.dictation > 0,
  recording: () => sessions.recording > 0,
};
const getServerSnapshot = () => false;

/** Non-hook read for event handlers. */
export function isRecordingListening(): boolean {
  return sessions.recording > 0;
}

/** True while a session of `source` (or any source, when omitted) is listening. */
export function useAudioListening(source?: ListeningSource): boolean {
  return useSyncExternalStore(subscribe, snapshots[source ?? 'any'], getServerSnapshot);
}
